import React, { useState, useEffect, useRef } from 'react';
import { useAsset } from '../../context/AssetContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Skeleton } from '../ui/skeleton';
import { ScrollArea } from '../ui/scroll-area';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { 
  Plus, Search, Upload, Download, MoreHorizontal, Pencil, Trash2, Loader2, 
  History, Package, FolderKanban, UserMinus, FileDown, FileUp, AlertTriangle, Info, Calendar,
  ArrowUp, ArrowDown, ArrowUpDown
} from 'lucide-react';
import * as XLSX from 'xlsx';

const EmployeeList = ({ onView }) => {
  const { 
    employees, lovs, assets, projects, isReady,
    fetchEmployees, fetchLOVs, fetchAssets, fetchProjects,
    createEmployee, updateEmployee, deleteEmployee,
    downloadEmployeeTemplate, importEmployees, exportEmployees,
    getEmployeeAssetAllocationHistory, getEmployeeAllocatedAssets,
    allocateAsset, unallocateAsset,
    allocateEmployeeToProject, unallocateEmployeeFromProject
  } = useAsset();

  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50, total: 0, total_pages: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState(null);        // 'employee_id' | 'employee_name' | 'email' | 'employee_status'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [allocateAssetDialogOpen, setAllocateAssetDialogOpen] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  // Import state - enhanced
  const [importStep, setImportStep] = useState('info'); // 'info', 'preview'

  const [formData, setFormData] = useState({
    employee_id: '',
    employee_name: '',
    email: '',
    employee_status: 'Employed'
  });

  const [importPreview, setImportPreview] = useState({ valid: [], invalid: [], file: null });
  
  // History data
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Delete confirmation with assets
  const [allocatedAssetsForDelete, setAllocatedAssetsForDelete] = useState([]);
  const [assetsToUnallocate, setAssetsToUnallocate] = useState([]);

  // Asset allocation
  const [selectedAssetToAllocate, setSelectedAssetToAllocate] = useState('');

  // Project allocation
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [currentEmployeeProject, setCurrentEmployeeProject] = useState(null);

  useEffect(() => {
    if (isReady) {
      loadEmployees();
      fetchLOVs();
      fetchAssets({}, 1, 200);
      fetchProjects({}, 1, 200);
    }
  }, [isReady]);

  useEffect(() => {
    if (!isReady) return;
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadEmployees(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, isReady]);

  // Reload on sort change (no debounce)
  useEffect(() => {
    if (!isReady) return;
    loadEmployees(1);
    setCurrentPage(1);
  }, [sortBy, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEmployees = async (page = currentPage) => {
    setLoading(true);
    const filters = {};
    if (searchQuery) filters.search = searchQuery;
    if (sortBy) { filters.sort_by = sortBy; filters.sort_order = sortOrder; }
    const result = await fetchEmployees(filters, page, 50);
    if (result.pagination) setPagination(result.pagination);
    setLoading(false);
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortBy !== field) return <ArrowUpDown className="ml-1 inline h-3 w-3 opacity-40" />;
    return sortOrder === 'asc'
      ? <ArrowUp className="ml-1 inline h-3 w-3" />
      : <ArrowDown className="ml-1 inline h-3 w-3" />;
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    loadEmployees(newPage);
  };

  const resetForm = () => {
    setFormData({ employee_id: '', employee_name: '', email: '', employee_status: 'Employed' });
  };

  const handleCreate = async () => {
    if (!formData.employee_id || !formData.employee_name || !formData.email || !formData.employee_status) {
      toast.error('Please fill in all required fields');
      return;
    }

    setFormLoading(true);
    const result = await createEmployee(formData);
    setFormLoading(false);

    if (result.success) {
      toast.success('Employee created successfully');
      setCreateDialogOpen(false);
      resetForm();
      loadEmployees();
    } else {
      toast.error(result.error);
    }
  };

  const handleEdit = async () => {
    setFormLoading(true);
    const { employee_id, ...updateData } = formData;
    const result = await updateEmployee(selectedEmployee.employee_id, updateData);
    setFormLoading(false);

    if (result.success) {
      toast.success('Employee updated successfully');
      setEditDialogOpen(false);
      resetForm();
      loadEmployees();
    } else {
      toast.error(result.error);
    }
  };

  const handleDelete = async () => {
    // If there are assets to unallocate first
    if (assetsToUnallocate.length > 0) {
      setFormLoading(true);
      for (const assetId of assetsToUnallocate) {
        await unallocateAsset(assetId);
      }
      // Refresh assets list
      await fetchAssets({}, 1, 200);
    }

    setFormLoading(true);
    const result = await deleteEmployee(selectedEmployee.employee_id);
    setFormLoading(false);

    if (result.success) {
      toast.success('Employee deleted successfully');
      setDeleteDialogOpen(false);
      setAllocatedAssetsForDelete([]);
      setAssetsToUnallocate([]);
      loadEmployees();
    } else {
      toast.error(result.error);
    }
  };

  const openEditDialog = (employee) => {
    setSelectedEmployee(employee);
    setFormData({ ...employee });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = async (employee) => {
    setSelectedEmployee(employee);
    setAssetsToUnallocate([]);
    
    // Get allocated assets for this employee
    const result = await getEmployeeAllocatedAssets(employee.employee_id);
    setAllocatedAssetsForDelete(result.assets || []);
    setDeleteDialogOpen(true);
  };

  const openHistoryDialog = async (employee) => {
    setSelectedEmployee(employee);
    setHistoryLoading(true);
    setHistoryDialogOpen(true);
    
    const history = await getEmployeeAssetAllocationHistory(employee.employee_id);
    setHistoryData(history);
    setHistoryLoading(false);
  };

  const openAllocateAssetDialog = (employee) => {
    setSelectedEmployee(employee);
    setSelectedAssetToAllocate('');
    setAllocateAssetDialogOpen(true);
  };

  const openProjectDialog = async (employee) => {
    setSelectedEmployee(employee);
    setSelectedProjectId('');
    
    // Find current project
    const empProject = projects.find(p => 
      p.allocated_employees?.some(e => e.employee_id === employee.employee_id)
    );
    setCurrentEmployeeProject(empProject || null);
    setProjectDialogOpen(true);
  };

  const handleAllocateAsset = async () => {
    if (!selectedAssetToAllocate) {
      toast.error('Please select an asset');
      return;
    }

    setFormLoading(true);
    const result = await allocateAsset(selectedAssetToAllocate, selectedEmployee.employee_id);
    setFormLoading(false);

    if (result.success) {
      toast.success('Asset allocated successfully');
      setAllocateAssetDialogOpen(false);
      fetchAssets({}, 1, 200);
    } else {
      toast.error(result.error);
    }
  };

  const handleProjectAllocation = async () => {
    setFormLoading(true);
    
    if (selectedProjectId) {
      const result = await allocateEmployeeToProject(selectedEmployee.employee_id, selectedProjectId);
      if (result.success) {
        toast.success('Employee allocated to project');
        setProjectDialogOpen(false);
        fetchProjects({}, 1, 200);
      } else {
        toast.error(result.error);
      }
    }
    
    setFormLoading(false);
  };

  const handleUnallocateFromProject = async () => {
    setFormLoading(true);
    const result = await unallocateEmployeeFromProject(selectedEmployee.employee_id);
    setFormLoading(false);

    if (result.success) {
      toast.success('Employee unallocated from project');
      setCurrentEmployeeProject(null);
      fetchProjects({}, 1, 200);
    } else {
      toast.error(result.error);
    }
  };

  // Enhanced Import
  const openImportDialog = () => {
    setImportStep('info');
    setImportPreview({ valid: [], invalid: [], file: null });
    setImportDialogOpen(true);
  };

  const handleDownloadTemplate = async () => {
    setFormLoading(true);
    const result = await downloadEmployeeTemplate();
    setFormLoading(false);
    if (result.success) {
      toast.success('Template downloaded');
    } else {
      toast.error(result.error || 'Failed to download template');
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please select an Excel file (.xlsx)');
      return;
    }

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { raw: false, defval: '' });

      const valid = [];
      const invalid = [];

      rows.forEach((row, idx) => {
        const errors = [];
        if (!row.employee_id) errors.push('Employee ID required');
        if (!row.employee_name) errors.push('Employee Name required');
        if (!row.email) errors.push('Email required');
        if (!row.employee_status) errors.push('Employee Status required');

        if (errors.length > 0) {
          invalid.push({ row: idx + 2, data: row, errors });
        } else {
          valid.push(row);
        }
      });

      setImportPreview({ valid, invalid, file });
      setImportStep('preview');
    } catch (err) {
      toast.error('Failed to parse file');
    }
  };

  const handleImport = async () => {
    if (!importPreview.file) return;

    setFormLoading(true);
    const result = await importEmployees(importPreview.file);
    setFormLoading(false);

    if (result.success) {
      toast.success(`Imported ${result.data.valid_count} employees`);
      setImportDialogOpen(false);
      setImportPreview({ valid: [], invalid: [], file: null });
      loadEmployees();
    } else {
      toast.error(result.error);
    }
  };

  const handleExport = async () => {
    const result = await exportEmployees();
    if (result.success) {
      toast.success('Employees exported successfully');
    } else {
      toast.error(result.error);
    }
  };

  // Import columns config
  const importColumns = [
    { name: 'employee_id', required: true, description: 'Unique identifier (e.g., EMP001)' },
    { name: 'employee_name', required: true, description: 'Full name of the employee' },
    { name: 'email', required: true, description: 'Email address (e.g., john@company.com)' },
    { name: 'employee_status', required: true, description: 'Status: Employed, Resigned, On Leave' },
  ];

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'Employed': return 'default';
      case 'Resigned': return 'secondary';
      case 'Departed': return 'outline';
      default: return 'outline';
    }
  };

  const availableAssets = Array.isArray(assets) ? assets.filter(a => !a.allocated_to) : [];
  const activeProjects = Array.isArray(projects) ? projects.filter(p => p.is_active) : [];

  const toggleAssetUnallocate = (assetId) => {
    setAssetsToUnallocate(prev => 
      prev.includes(assetId) 
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  return (
    <div className="space-y-6" data-testid="employee-list">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Employees</h2>
          <p className="text-muted-foreground">Manage employees for asset allocation</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openImportDialog}>
            <Upload className="mr-2 h-4 w-4" /> Import
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" /> Export
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add Employee
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by ID, Name, or Email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Employees</span>
            <Badge variant="secondary">{pagination?.total || 0} records</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : !Array.isArray(employees) || employees.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No employees found. Add your first employee to get started.
            </div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('employee_id')} data-testid="emp-list-sort-id">
                      Employee ID{renderSortIcon('employee_id')}
                    </TableHead>
                    <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('employee_name')} data-testid="emp-list-sort-name">
                      Name{renderSortIcon('employee_name')}
                    </TableHead>
                    <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('email')} data-testid="emp-list-sort-email">
                      Email{renderSortIcon('email')}
                    </TableHead>
                    <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('employee_status')} data-testid="emp-list-sort-status">
                      Status{renderSortIcon('employee_status')}
                    </TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map((emp) => (
                    <TableRow
                      key={emp?.employee_id || Math.random()}
                      className={onView ? 'cursor-pointer hover:bg-accent' : ''}
                      onClick={() => onView && emp?.employee_id && onView(emp)}
                      data-testid={`emp-row-${emp?.employee_id}`}
                    >
                      <TableCell className="font-mono">{emp?.employee_id}</TableCell>
                      <TableCell className="font-medium">{emp?.employee_name}</TableCell>
                      <TableCell>{emp?.email}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(emp?.employee_status)}>
                          {emp?.employee_status}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(emp)}>
                              <Pencil className="mr-2 h-4 w-4" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openHistoryDialog(emp)}>
                              <History className="mr-2 h-4 w-4" /> Allocation History
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => openAllocateAssetDialog(emp)}>
                              <Package className="mr-2 h-4 w-4" /> Allocate Asset
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openProjectDialog(emp)}>
                              <FolderKanban className="mr-2 h-4 w-4" /> Manage Project
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => openDeleteDialog(emp)} className="text-red-600">
                              <Trash2 className="mr-2 h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {!loading && Array.isArray(employees) && employees.length > 0 && pagination?.total_pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">Page {pagination.page} of {pagination.total_pages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page <= 1}>Previous</Button>
                <Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page >= pagination.total_pages}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={createDialogOpen || editDialogOpen} onOpenChange={(open) => {
        if (!open) { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editDialogOpen ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Employee ID *</Label>
              <Input value={formData.employee_id} onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })} disabled={editDialogOpen} placeholder="e.g., EMP001" />
            </div>
            <div className="space-y-2">
              <Label>Employee Name *</Label>
              <Input value={formData.employee_name} onChange={(e) => setFormData({ ...formData, employee_name: e.target.value })} placeholder="Full name" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="email@company.com" />
            </div>
            <div className="space-y-2">
              <Label>Status *</Label>
              <Select value={formData.employee_status} onValueChange={(v) => setFormData({ ...formData, employee_status: v })}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  {lovs.employee_statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }}>Cancel</Button>
            <Button onClick={editDialogOpen ? handleEdit : handleCreate} disabled={formLoading}>
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editDialogOpen ? 'Save Changes' : 'Create Employee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog with Allocated Assets */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Delete Employee
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedEmployee?.employee_name}"?
            </DialogDescription>
          </DialogHeader>
          
          {allocatedAssetsForDelete.length > 0 && (
            <div className="border rounded-lg p-4 bg-amber-50">
              <p className="font-medium text-amber-800 mb-3">
                This employee has {allocatedAssetsForDelete.length} allocated asset(s):
              </p>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {allocatedAssetsForDelete.map((asset) => (
                  <div key={asset.asset_id} className="flex items-center gap-2 p-2 bg-white rounded border">
                    <Checkbox
                      checked={assetsToUnallocate.includes(asset.asset_id)}
                      onCheckedChange={() => toggleAssetUnallocate(asset.asset_id)}
                    />
                    <div className="flex-1">
                      <span className="font-mono text-sm">{asset.asset_id}</span>
                      <span className="text-muted-foreground"> - {asset.asset_name}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-amber-700 mt-3">
                Select assets to unallocate before deleting the employee.
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={formLoading || (allocatedAssetsForDelete.length > 0 && assetsToUnallocate.length !== allocatedAssetsForDelete.length)}
            >
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {assetsToUnallocate.length > 0 ? `Unallocate & Delete` : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Allocation History Dialog (asset-centric, paired rows) */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Allocation History - {selectedEmployee?.employee_name}</DialogTitle>
            <DialogDescription>
              Every asset {selectedEmployee?.employee_id} has been allocated, with current status.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[440px] pr-4">
            {historyLoading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            ) : historyData.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="employee-alloc-history-empty">
                This employee has never been allocated any asset.
              </div>
            ) : (
              <div className="space-y-2" data-testid="employee-alloc-history-list">
                {historyData.map((row, idx) => (
                  <div
                    key={`${row.asset_id}-${idx}`}
                    className="border rounded-lg p-3 flex items-start justify-between flex-wrap gap-3"
                    data-testid={`employee-alloc-history-row-${idx}`}
                  >
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <Package className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="text-sm font-mono font-medium" data-testid={`employee-alloc-history-asset-id-${idx}`}>
                            {row.asset_id}
                          </span>
                          {row.asset_type && (
                            <Badge variant="outline" className="text-[10px]">{row.asset_type}</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground break-words">
                          {row.asset_name || <span className="italic">Asset deleted</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {row.allocated_at ? new Date(row.allocated_at).toLocaleString() : '—'}
                      </span>
                      <Badge
                        variant={row.status === 'Active' ? 'default' : 'outline'}
                        data-testid={`employee-alloc-history-status-${idx}`}
                      >
                        {row.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocate Asset Dialog */}
      <Dialog open={allocateAssetDialogOpen} onOpenChange={setAllocateAssetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Allocate Asset to {selectedEmployee?.employee_name}</DialogTitle>
            <DialogDescription>Select an available asset to allocate</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Available Assets</Label>
            <Select value={selectedAssetToAllocate} onValueChange={setSelectedAssetToAllocate}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select an asset" />
              </SelectTrigger>
              <SelectContent>
                {availableAssets.length === 0 ? (
                  <SelectItem value="none" disabled>No available assets</SelectItem>
                ) : (
                  availableAssets.map(a => (
                    <SelectItem key={a.asset_id} value={a.asset_id}>
                      {a.asset_id} - {a.asset_name} ({a.asset_type})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAllocateAssetDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAllocateAsset} disabled={formLoading || !selectedAssetToAllocate}>
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Allocate Asset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project Allocation Dialog */}
      <Dialog open={projectDialogOpen} onOpenChange={setProjectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Project for {selectedEmployee?.employee_name}</DialogTitle>
            <DialogDescription>Allocate or unallocate employee from a project</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {currentEmployeeProject && (
              <div className="border rounded-lg p-4 bg-blue-50">
                <p className="text-sm font-medium text-blue-800 mb-2">Currently allocated to:</p>
                <div className="flex items-center justify-between">
                  <span className="font-mono">{currentEmployeeProject.project_id} - {currentEmployeeProject.project_name}</span>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleUnallocateFromProject}
                    disabled={formLoading}
                  >
                    {formLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserMinus className="h-4 w-4 mr-1" />}
                    Unallocate
                  </Button>
                </div>
              </div>
            )}
            
            <div>
              <Label>Allocate to Project</Label>
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  {activeProjects.length === 0 ? (
                    <SelectItem value="none" disabled>No active projects</SelectItem>
                  ) : (
                    activeProjects
                      .filter(p => p.project_id !== currentEmployeeProject?.project_id)
                      .map(p => (
                        <SelectItem key={p.project_id} value={p.project_id}>
                          {p.project_id} - {p.project_name}
                        </SelectItem>
                      ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProjectDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleProjectAllocation} disabled={formLoading || !selectedProjectId}>
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Allocate to Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enhanced Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Import Employees</DialogTitle>
            <DialogDescription>
              {importStep === 'info' && 'Review the required columns and download a template'}
              {importStep === 'preview' && `${importPreview.valid.length} valid, ${importPreview.invalid.length} invalid records`}
            </DialogDescription>
          </DialogHeader>

          {importStep === 'info' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Info className="h-5 w-5 text-blue-600" />
                  <h4 className="font-medium text-blue-800">Excel File Requirements</h4>
                </div>
                <p className="text-sm text-blue-700 mb-3">
                  Your Excel file should have the following columns in the first row (header row):
                </p>
              </div>

              <ScrollArea className="h-[200px] pr-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Column Name</TableHead>
                      <TableHead>Required</TableHead>
                      <TableHead>Description</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importColumns.map((col) => (
                      <TableRow key={col.name}>
                        <TableCell className="font-mono text-sm">{col.name}</TableCell>
                        <TableCell>
                          {col.required ? (
                            <Badge variant="destructive" className="text-xs">Required</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Optional</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{col.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>

              <div className="flex items-center justify-between pt-4 border-t">
                <Button variant="outline" onClick={handleDownloadTemplate} disabled={formLoading} className="gap-2">
                  {formLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                  Download Sample Template
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setImportDialogOpen(false)}>Cancel</Button>
                  <Button onClick={() => fileInputRef.current?.click()} className="gap-2">
                    <FileUp className="h-4 w-4" />
                    Select File to Upload
                  </Button>
                  <input 
                    ref={fileInputRef} 
                    type="file" 
                    accept=".xlsx,.xls" 
                    className="hidden" 
                    onChange={handleFileSelect} 
                  />
                </div>
              </div>
            </div>
          )}

          {importStep === 'preview' && (
            <div className="space-y-4">
              <ScrollArea className="h-[400px] pr-4">
                {importPreview.invalid.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-medium text-red-600 mb-2">Invalid Records:</h4>
                    {importPreview.invalid.slice(0, 5).map((r, i) => (
                      <div key={i} className="text-sm bg-red-50 p-2 rounded mb-1">
                        Row {r.row}: {r.errors.join(', ')}
                      </div>
                    ))}
                    {importPreview.invalid.length > 5 && (
                      <p className="text-sm text-red-500 mt-2">
                        ... and {importPreview.invalid.length - 5} more invalid records
                      </p>
                    )}
                  </div>
                )}
                {importPreview.valid.length > 0 && (
                  <div>
                    <h4 className="font-medium text-green-600 mb-2">Valid Records ({importPreview.valid.length} total):</h4>
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Employee ID</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {importPreview.valid.slice(0, 10).map((r, i) => (
                            <TableRow key={i}>
                              <TableCell className="font-mono">{r.employee_id}</TableCell>
                              <TableCell>{r.employee_name}</TableCell>
                              <TableCell>{r.email}</TableCell>
                              <TableCell>{r.employee_status}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {importPreview.valid.length > 10 && (
                      <p className="text-sm text-muted-foreground mt-2">
                        Showing first 10 of {importPreview.valid.length} valid records
                      </p>
                    )}
                  </div>
                )}
              </ScrollArea>

              <DialogFooter className="border-t pt-4">
                <Button variant="outline" onClick={() => setImportStep('info')}>Back</Button>
                <Button variant="outline" onClick={() => setImportDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleImport} disabled={formLoading || importPreview.valid.length === 0}>
                  {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Import {importPreview.valid.length} Employees
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeList;
